import * as React from 'react'
import {Lang} from 'yougile-lang'
import {logger, Time} from 'yougile-util'
import {Hub, Money, PaymentAction, paymentDescr, User} from 'yougile-model'
import {TrackingEvent} from '@common/tracking-events'
import {Tracking} from '../../../common/tracking'
import {setDisplayName} from '../../../common/utils-ui'
import {PaymentClient} from '../../client/payment-client'
import {Sync} from '../../client/sync'
import {withUpdaters} from '../../containers/updaters'
import {SimpleUpdater} from '../../containers/updaters/simple-updater'
import {SyncUpdater} from '../../containers/updaters/sync-updater'
import {globals} from '../../extensions/globals-base'
import {PageService} from '../../extensions/page-service'
import {UserAccess} from '../../extensions/user-access'
import {Requisites, WebPay} from '../../extensions/web-pay'
import {MessageBox} from '../message-box'
import {BillingBase, BillingProps, calcTotal} from './billing-base'

const state: {license: BillingProps['license']; update: () => void} = {
  license: undefined,
  update: undefined
}

const getAction = (props: BillingProps): PaymentAction => {
  const {license} = props
  const amount: Money = {
    amount: calcTotal(props),
    currency: 'RUB'
  }
  switch (license.type) {
    case 'buy':
      return {
        type: 'buy',
        users: license.users,
        amount,
        date: Time.now(),
        tariff: 'standard',
        duration: {unit: 'm', amount: license.months}
      }
    case 'add-users':
      return {
        type: 'upgrade',
        amount,
        date: Time.now(),
        addUsers: state.license.users
      }
    default:
      return {
        type: 'renew',
        amount,
        date: Time.now(),
        duration: {unit: 'm', amount: license.months}
      }
  }
}

export const Billing = withUpdaters(
  SyncUpdater({companyType: 'current', filter: '*', debounceTime: 1000}),
  SimpleUpdater(update => (state.update = update))
)(() => {
  const box = Sync.getCurrentSandbox()
  const companyName = (box.get(box.companyId) as Hub).title
  const paymentStatus = PaymentClient.get(box.companyId)
  const user = Sync.getProfile().get(globals.currentUserId) as User

  React.useEffect(() => {
    UserAccess.fixDeletedUsers(box)
  }, [box?.companyId])

  const clearLicense = () => {
    state.license = {
      type: undefined,
      email: user.email,
      phone: globals.phone,
      error: '',
      months: 12,
      paymentMethod: 'card',
      success: false,
      recurrent: true,
      users: 5,
      wait: false
    }
  }

  React.useEffect(() => {
    clearLicense()
    const id = PageService.addLocationListener(clearLicense)
    const id2 = globals.onCompanyChange(clearLicense)
    return () => {
      PageService.removeLocationListener(id)
      globals.removeCompanyListener(id2)
    }
  }, [])
  if (!state.license) {
    clearLicense()
  }

  const {license} = state
  const onChange = (newLic: BillingProps['license']) => {
    state.license = newLic
    state.update()
  }
  const onPayCard = () => {
    UserAccess.fixDeletedUsers(box)
    const action = getAction(props)
    void Tracking.send(TrackingEvent.billingPayCardClick)
    license.wait = true
    state.update()
    const descr = paymentDescr(action, Lang.getLang())
    void WebPay.pay(
      box.companyId,
      action,
      paymentStatus,
      descr,
      license.email,
      license.phone,
      license.recurrent && license.months
    )
      .then(() => {
        void Tracking.send(TrackingEvent.billingPayCardSuccess)
        MessageBox.show('Success', 'success')
      })
      .catch(err => {
        if (err !== 'User has cancelled') {
          logger.warn('Error', err)
          MessageBox.show(err?.toString(), 'danger')
          void Tracking.send(TrackingEvent.billingPayCardError)
        } else {
          void Tracking.send(TrackingEvent.billingPayCardCancel)
        }
      })
      .then(() => {
        license.wait = false
        state.update()
      })
  }

  const onRequestInvoice = (requisites: Requisites) => {
    UserAccess.fixDeletedUsers(box)
    const action = getAction(props)
    void Tracking.send(TrackingEvent.billingInvoiceClick)
    license.wait = true
    state.update()
    void WebPay.requestInvoice(
      box.companyId,
      action,
      license.email,
      license.phone,
      requisites
    )
      .then(() => {
        MessageBox.show(Lang.msg.success, 'success')
        license.success = true
        void Tracking.send(TrackingEvent.billingInvoiceSuccess)
      })
      .catch(err => {
        logger.warn('Error', err)
        MessageBox.show(err?.toString(), 'danger')
        void Tracking.send(TrackingEvent.billingInvoiceError)
      })
      .then(() => {
        license.wait = false
        state.update()
      })
  }

  const props: BillingProps = {
    companyName,
    paymentStatus,
    license,
    onChange,
    onPayCard,
    onRequestInvoice
  }

  return <BillingBase {...props} />
})

setDisplayName(Billing, 'Billing')
